import {empty as observableEmpty, Observable} from 'rxjs';

import {mergeMap, take} from 'rxjs/operators';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {ArtifactId, BrowserSessionStateId, ExpeditionId} from '../../types/ids';
import {DateTime} from '../../types/base-types';


import moment from 'moment';
import {isDefined} from '../../utils/utils';
import {BrowserSessionInfo, BrowserSessionStateArtifact, SpanId} from '../../types/browsersession';
import {BrowserSessionService} from '../../services/browsersession.service';
import {ArtifactService} from '../../services/artifact.service';
import {Artifact, ArtifactInfo} from '../../types/artifact';
import {WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';

export class BrowsersessionViewerToolConfig implements WebmateToolConfig {
    toolName = 'BrowserSession';

    constructor(public browserSessionId: ExpeditionId) {
    }
}


@Component({
    selector: 'wm-browsersessionviewer',
    templateUrl: './browsersessionviewer.component.html',
    styleUrls: ['./browsersessionviewer.component.less']
})
export class BrowsersessionViewerComponent implements OnInit {

    @Input() browsersessionId: ExpeditionId;

    browserSession$: Observable<BrowserSessionInfo> = observableEmpty();
    browserSession: BrowserSessionInfo;

    stateArtifacts: BrowserSessionStateArtifact[];

    stateToArtifacts: Map<BrowserSessionStateId, ArtifactId[]>;

    artifactsToState: Map<ArtifactId, BrowserSessionStateId>;

    states: BrowserSessionStateId[];

    artifacts: Map<ArtifactId, ArtifactInfo>;

    artifactsWithoutState: ArtifactId[];

    actionSpans: Map<SpanId, ArtifactId[]> = new Map();

    actionSpansToColors: Map<SpanId, string> = new Map();

    activeArtifact: ArtifactId;

    x: boolean;

    duration: string = "";

    loadingNewArtifacts = true;

    constructor(@Inject(TOOL_CONTAINER_DATA) private data: any, private browserSessionService: BrowserSessionService, private artifactService: ArtifactService) {
        if (isDefined(data)) {
            this.browsersessionId = data.browserSessionId;
        }
    }

    intervalHandle: number;

    ngOnInit() {
        let queryCallback = () => {
            this.loadingNewArtifacts = true;
            this.browserSession$ = this.browserSessionService.getBrowserSessionInfoNoCache(this.browsersessionId);
            this.browserSession$.pipe(
                mergeMap((browserSession: BrowserSessionInfo) => {
                    this.browserSession = browserSession;
                    return this.artifactService.getArtifactsInBrowserSession$(this.browsersessionId);
                }),
                mergeMap((artifacts: ArtifactInfo[]) => {
                    this.artifacts = new Map(artifacts
                        .slice()
                        .sort((a, b) => moment(a!.creationTime).valueOf() - moment(b!.creationTime).valueOf())
                        .map((artifact) => [artifact!.id, artifact]));
                    return this.browserSessionService.mapSessionArtifactsToStates$(this.browsersessionId)
                }),
                mergeMap((artifactToStateMapping: Map<BrowserSessionStateId, ArtifactId[]>) => {
                    this.stateToArtifacts = artifactToStateMapping;
                    return this.browserSessionService.getBrowserSessionStates(this.browsersessionId);
                }),
                mergeMap((stateIds: BrowserSessionStateId[]) => {
                    this.states = stateIds;
                    return this.artifactService.getArtifacts$(stateIds)
                }),
                take(1)
            ).subscribe((stateArtifacts: Artifact[]) => {
                this.loadingNewArtifacts = false;
                this.stateArtifacts = stateArtifacts
                    .map(a => BrowserSessionStateArtifact.fromArtifact(a!))
                    .sort((a, b) => moment(a!.creationTime).valueOf() - moment(b!.creationTime).valueOf());

                this.artifactsToState = new Map<ArtifactId, BrowserSessionStateId>();
                this.stateToArtifacts.forEach((artifacts, state) => {
                    artifacts.forEach(artifact => {
                        this.artifactsToState.set(artifact, state);
                    });
                });

                const allArtifactIds = Array.from(this.artifacts.keys());
                const artifactsWithState = new Set(this.artifactsToState.keys());
                this.artifactsWithoutState = allArtifactIds
                    .filter(artId => !artifactsWithState.has(artId))
                    .filter(artId => this.artifacts.get(artId!)?.artifactType !== 'Action.BrowserSessionState')
                    .sort((a, b) => moment(this.artifacts.get(a!)?.creationTime).valueOf() - moment(this.artifacts.get(b!)?.creationTime).valueOf());
                this.diffDates();

                if (!this.browserSession.isActive()) {
                    clearInterval(this.intervalHandle);
                }
            });
        };

        queryCallback();
        this.intervalHandle = window.setInterval(queryCallback, 10000);
    }

    ngOnDestroy() {
        if (isDefined(this.states)) {
            this.states.forEach(id => {
                let artifacts = this.stateToArtifacts.get(id!);
                if (isDefined(artifacts)) {
                    artifacts.forEach(aid => this.browserSessionService.evictArtifact(aid!));
                    this.browserSessionService.evictState(id!);
                }
            });
        }
    }

    /**
     * Determines if the Span should be visually displayed (left border)
     */
    isSpanShown(spanId: SpanId): boolean {
        return this.actionSpans.has(spanId);
    }

    /**
     * Find the a set of artifacts that should be displayed together given an activated artifact. Currently, this is
     * the set of all artifacts in the same span as the activated artifact.
     */
    computeDisplayedArtifacts(artifactId: ArtifactId): ArtifactId[] {
        return [artifactId];
    }

    diffDates() {
        let dateDiff = moment.duration(moment(this.browserSession!.optEnd!).diff(moment(this.browserSession!.optStart!)));
        let hours = dateDiff.hours();
        let minutes = dateDiff.minutes();
        let seconds = dateDiff.seconds();

        this.duration =  (hours ? hours + 'h' : '') + (minutes ? minutes + 'm' : '') + seconds + 's';
    }

    timeFromStart(t: DateTime) {
        let dateDiff = moment.duration(moment(t).diff(moment(this.browserSession.optStart!)));
        let hours = dateDiff.hours();
        let minutes = dateDiff.minutes();
        let seconds = dateDiff.seconds();

        return (hours ? hours + 'h' : '') + (minutes ? minutes + 'm' : '') + seconds + 's';
    }

}
